import { ChevronDown } from 'lucide-react';
import { useCallback, useRef, useState, type ComponentType } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useVisualization } from '@/context/visualization-context';
import { cn } from '@/lib/utils';
import { WINDOW_LEVEL_PRESETS } from '@/visualization/services/window-level-presets';
import { WindowLevelService } from '@/visualization/services/window-level-service';

export interface WindowLevelToolButtonProps {
    isActive: boolean;
    onActivate: () => void;
    icon: ComponentType<{ className?: string }>;
    description: string;
}

/**
 * W/L split-button: the left button activates the drag tool; the chevron opens a
 * popover with presets and editable custom W/L fields. Hovering a preset live-previews
 * it on the real viewports; the value is kept only on selection (commit) and reverted
 * on close (cancel). Editing the custom fields applies live and is kept.
 *
 * A popover (not a dropdown menu) is used because the menu primitive traps/roves focus
 * between its items and won't let the number inputs hold the cursor. All preview/commit
 * state lives in WindowLevelService (visualization rule 5).
 */
export function WindowLevelToolButton({
    isActive,
    onActivate,
    icon: Icon,
    description,
}: WindowLevelToolButtonProps) {
    const { getServiceManager } = useVisualization();
    const committedRef = useRef(false);
    const [open, setOpen] = useState(false);
    // Preset matching the committed W/L, captured when the popover opens so it shows
    // immediately and stays stable while hovering (previewing) other presets.
    const [committedPresetId, setCommittedPresetId] = useState<string | null>(null);
    // Custom W/L inputs (strings to allow empty/intermediate edits), seeded on open.
    const [customWindow, setCustomWindow] = useState<string>('');
    const [customLevel, setCustomLevel] = useState<string>('');

    const getService = useCallback(
        () => getServiceManager().getService<WindowLevelService>(WindowLevelService.SERVICE_NAME),
        [getServiceManager]
    );

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            setOpen(nextOpen);
            const wl = getService();
            if (nextOpen) {
                committedRef.current = false;
                wl.startPreview();
                const { window, level } = wl.getWindowLevel();
                setCustomWindow(String(Math.floor(window)));
                setCustomLevel(String(Math.floor(level)));
                setCommittedPresetId(wl.getActivePresetId()); // sync highlight on open
            } else if (committedRef.current) {
                wl.commitPreview();
                committedRef.current = false;
            } else {
                wl.cancelPreview();
            }
        },
        [getService]
    );

    const preview = useCallback(
        (window: number, level: number) => getService().previewWindowLevel(window, level),
        [getService]
    );
    const revert = useCallback(() => getService().revertPreview(), [getService]);
    const previewDefault = useCallback(() => {
        const wl = getService();
        const { window, level } = wl.getDefaultWindowLevel();
        wl.previewWindowLevel(window, level);
    }, [getService]);
    const commitPreset = useCallback(
        (window: number, level: number) => {
            const wl = getService();
            wl.previewWindowLevel(window, level);
            // Lock the value in now (clear the revert point) rather than relying on the
            // close handler: as the popover unmounts the hovered button fires a stray
            // pointerleave → revert(), which would otherwise restore the pre-click value
            // (e.g. a prior custom edit) and lose the preset just chosen.
            wl.commitPreview();
            committedRef.current = true;
            setOpen(false);
        },
        [getService]
    );
    const handleReset = useCallback(() => {
        const wl = getService();
        wl.resetToDefaults();
        wl.commitPreview(); // lock in the reset before the popover closes (see commitPreset)
        committedRef.current = true;
        setOpen(false);
    }, [getService]);

    // Apply a typed value live and keep it (no Apply button): rebase the preview
    // revert-point to the typed value so hovering a preset still reverts back here,
    // and mark committed so it survives the popover closing. Empty/non-numeric
    // intermediate edits (e.g. "-", "") are skipped until they parse.
    const applyCustomValue = useCallback(
        (windowStr: string, levelStr: string) => {
            if (windowStr.trim() === '' || levelStr.trim() === '') return;
            const window = Number(windowStr);
            const level = Number(levelStr);
            if (!Number.isFinite(window) || !Number.isFinite(level)) return;
            const wl = getService();
            wl.previewWindowLevel(window, level);
            wl.commitPreview();
            wl.startPreview();
            committedRef.current = true;
            setCommittedPresetId(wl.getActivePresetId());
        },
        [getService]
    );

    return (
        <div className="flex items-center">
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant={isActive ? 'default' : 'ghost'}
                        size="icon-sm"
                        onClick={onActivate}
                        className={cn(
                            'h-7 w-7 rounded-r-none',
                            !isActive && 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Icon className="h-3.5 w-3.5" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{description}</TooltipContent>
            </Tooltip>
            <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                    <Button
                        variant={isActive ? 'default' : 'ghost'}
                        size="icon-sm"
                        className={cn(
                            'h-7 w-4 rounded-l-none border-l-0 px-0',
                            !isActive && 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <ChevronDown className="h-3 w-3" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    side="bottom"
                    align="start"
                    className="w-56 p-1"
                    // The viewport auto-focuses itself on pointerenter (for keydown shortcuts),
                    // which steals focus from the popover the moment the cursor leaves it. Keep
                    // the popover open on focus-out; a real outside click still dismisses it.
                    onFocusOutside={(e) => e.preventDefault()}
                    // Don't auto-focus the first preset on open — its onFocus handler would
                    // preview that preset (e.g. 800/200) before the user hovers anything.
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <div className="px-2 py-1.5 text-sm font-semibold">Presets</div>
                    {WINDOW_LEVEL_PRESETS.map((preset) => (
                        <button
                            key={preset.id}
                            type="button"
                            onPointerEnter={() => preview(preset.window, preset.level)}
                            onFocus={() => preview(preset.window, preset.level)}
                            onPointerLeave={revert}
                            onClick={() => commitPreset(preset.window, preset.level)}
                            className={cn(
                                'flex w-full items-center justify-between gap-4 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent/50 focus-visible:bg-accent/50',
                                committedPresetId === preset.id && 'bg-accent text-accent-foreground'
                            )}
                        >
                            <span>{preset.label}</span>
                            <span className="text-xs text-muted-foreground tabular-nums">
                                {preset.window}/{preset.level}
                            </span>
                        </button>
                    ))}
                    <div className="-mx-1 my-1 h-px bg-muted" />
                    <div className="px-2 py-1.5 text-sm font-semibold">Custom</div>
                    <div className="flex items-center gap-2 px-2 py-1.5">
                        <label className="flex flex-1 items-center gap-1.5 text-xs text-muted-foreground">
                            W
                            <Input
                                type="number"
                                value={customWindow}
                                onChange={(e) => {
                                    setCustomWindow(e.target.value);
                                    applyCustomValue(e.target.value, customLevel);
                                }}
                                className="h-7 px-2 text-xs tabular-nums"
                            />
                        </label>
                        <label className="flex flex-1 items-center gap-1.5 text-xs text-muted-foreground">
                            L
                            <Input
                                type="number"
                                value={customLevel}
                                onChange={(e) => {
                                    setCustomLevel(e.target.value);
                                    applyCustomValue(customWindow, e.target.value);
                                }}
                                className="h-7 px-2 text-xs tabular-nums"
                            />
                        </label>
                    </div>
                    <div className="-mx-1 my-1 h-px bg-muted" />
                    <button
                        type="button"
                        onPointerEnter={previewDefault}
                        onFocus={previewDefault}
                        onPointerLeave={revert}
                        onClick={handleReset}
                        className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent/50 focus-visible:bg-accent/50"
                    >
                        Reset to defaults
                    </button>
                </PopoverContent>
            </Popover>
        </div>
    );
}
